import '../styles/galaxy.css';
import '../styles/landing.css';

const RELEASES = [
  {
    version: '1.10',
    date: 'July 2026',
    title: 'Apple Music Support',
    items: [
      'Connect your Apple Music account to Stellar for a fully integrated listening experience.',
      'Export playlists directly to your Apple Music library — tracks are matched against the Apple Music catalog and saved as a new playlist you can open right in the app.',
      'Track and album links now resolve to canonical Apple Music pages for connected users.',
    ],
  },
  {
    version: '1.6',
    date: 'May 2026',
    title: 'Quick Seeds, Bigger Imports & Smarter Collisions',
    items: [
      'Added a "+ related artist" button to quickly choose artists to generate maps.',
      'Added 250 and 500 artist import options for Last.fm users.',
      'Improved the Collide compatibility score to increase the weighting of the number of shared artists.',
    ],
  },
  {
    version: '1.5',
    date: 'April 2026',
    title: 'Universe Spotlight & Polish',
    items: [
      'Universe Spotlight: a recommendation card on the My Universe tab surfaces a daily ranked artist with play, save, dislike, and Next controls — cycles without repeating.',
      'Friend match % is persisted server-side and stays consistent across devices, refreshing automatically when a new collision snapshot lands.',
      'Email notification when someone accepts your friend request.',
      'Saved map cards now feature a "% explored" hero stat, and the galaxy header shows your total explored artist count.',
      '"Remember Me" option on login keeps you signed in across sessions.',
      'Dismissible iOS app banner on the web prompts you to install the native app.',
      'Self-hosted Inter and Space Grotesk fonts eliminate layout shifts from Google Fonts fallback.',
      'Smoother canvas interactions on mobile: wider touch hit radius, slack drag threshold, and suppressed synthesized mouse events for more reliable taps.',
      'Fully-exploring a galaxy now triggers a celebration in the unknown artists pill.',
      'Faster, smoother collision intro animation with deferred effects through the intro and reduced LOD 2 stutter.',
    ],
  },
  {
    version: '2.0',
    date: 'March 2026',
    title: 'Collisions & Animations',
    items: [
      'Collision galaxies merge two users\' maps into one — shared frontier artists form a network shell around exclusive zones, all powered by d3-force simulations.',
      'Collision intro animation: two superclusters slide inward and converge with a flash effect.',
      'Big Bang intro animation for universe galaxies — nodes expand outward from a central singularity with cluster hazes, cosmic filaments, and a starfield fading in progressively.',
      'Collision playlist drawer with zone badges — colored dots and labels distinguish Shared Frontier tracks from each user\'s Exploration zone.',
    ],
  },
  {
    version: '1.9',
    date: 'March 2026',
    title: 'Invites & Social',
    items: [
      'Invite friends with shareable sign-up links — new users who register through your link are automatically added as friends.',
      'Share discovered artists as branded 1080×1080 images with artist photo, name, gold accent ring, and Stellar watermark. Uses Web Share API on mobile with download fallback on desktop.',
      'Friend cards now show profile pictures instead of letter initials, with graceful fallback when no avatar is set.',
    ],
  },
  {
    version: '1.8',
    date: 'March 2026',
    title: 'Mobile & Playback Polish',
    items: [
      'Artist detail panel collapses to a compact header on mobile — tap to expand up to 65 vh with a sticky scroll fade and animated chevron.',
      'Playlist auto-scroll keeps the current track visible; manual scrolling pauses auto-scroll for 4 seconds.',
      'Audio reliability: stale Deezer preview URLs are automatically refetched and cross-instance coordination prevents overlapping audio.',
      'Stellar is now at stellarmusic.io — old links redirect automatically.',
    ],
  },
  {
    version: '1.7',
    date: 'February 2026',
    title: 'Known Artists & Discovery Tracking',
    items: [
      'Mark artists as "Known" from the detail panel — known nodes dim on the canvas so new discoveries stand out.',
      'Mark artists as "Discovered on Stellar" — discovered nodes get a gold gradient ring on the canvas.',
      'An "unknown artists" badge on the galaxy map shows how many artists you haven\'t interacted with yet, updating dynamically as you explore.',
      '"Discovered on Stellar" section on the landing page — a horizontal scroll of your discoveries, click to add as seed.',
      'Favoriting or disliking an artist auto-marks them as known. Known and discovered are mutually exclusive.',
    ],
  },
  {
    version: '1.6',
    date: 'February 2026',
    title: 'Batch Seed Selection',
    items: [
      'Queue multiple artists as new seeds without reloading the map — click "+" to queue, click again to unqueue.',
      'A "Regenerate Map" button appears with a count of queued artists, triggering a single reload for all new seeds at once.',
      'Zoom controls added to the bottom-left for quick +/- zooming.',
      'Release notes now accessible from the galaxy view header.',
    ],
  },
  {
    version: '1.4.5',
    date: 'February 2026',
    title: 'Expand Universe',
    items: [
      'Expand Universe discovers genre-adjacent "drift" artists in the outer orbit of your galaxy — outliers that share musical DNA with your seeds but were never found through similarity chains.',
      'Drift nodes render as coral ring-shaped outlines, visually distinct at the edge of your map.',
      'The canvas background subtly shifts when expanded, marking the boundary between your core galaxy and the drift territory.',
    ],
  },
  {
    version: '1.4',
    date: 'February 2026',
    title: 'Share Playlist & Autoplay',
    items: [
      'Share Playlist lets you export your galaxy as a playable playlist with 30-second previews.',
      'Autoplay mode (on by default) continues playing previews sequentially down the list after your first play.',
      'Tracks without previews are automatically skipped.',
    ],
  },
  {
    version: '1.3',
    date: 'February 2026',
    title: 'Share Galaxy Maps',
    items: [
      'Share your galaxy as a beautiful image with a link, or copy a shareable URL.',
      'Captured images include a subtle Stellar watermark.',
    ],
  },
  {
    version: '1.2',
    date: 'January 2026',
    title: 'Hidden Gems & Chain Bridges',
    items: [
      'Hidden Gems surface artists with niche followings found through indirect paths — deep cuts from intermediate artists and bridges between styles.',
      'Chain Bridges connect distant genres step-by-step, revealing how different musical worlds relate.',
      'Galaxy Info modal explains every node type and link style.',
    ],
  },
  {
    version: '1.1',
    date: 'January 2026',
    title: 'Favorites & Galaxy Saves',
    items: [
      'Favorite artists directly from the detail panel to build a personal collection.',
      'Save and reload galaxy maps to revisit your discoveries later.',
      'Dislike artists to filter them from future recommendations.',
    ],
  },
  {
    version: '1.0',
    date: 'January 2026',
    title: 'Launch',
    items: [
      'Generate galaxy maps from up to 5 seed artists using Last.fm similarity data and Deezer enrichment.',
      'Interactive force-directed canvas with pan, zoom, and click-to-explore.',
      'Artist detail panel with genres, popularity, and 30-second audio previews.',
    ],
  },
];

function ReleaseNotesModal({ onClose }) {
  return (
    <div className="galaxy-info-overlay" onClick={onClose}>
      <div className="release-notes-modal" onClick={(e) => e.stopPropagation()}>
        <button className="galaxy-info-close" onClick={onClose}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="18" height="18">
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>

        <h3 className="galaxy-info-title">Release Notes</h3>

        <div className="release-notes-list">
          {RELEASES.map((release) => (
            <div key={`${release.version}-${release.date}`} className="release-notes-entry">
              <div className="release-notes-header">
                <span className="release-notes-date">{release.date}</span>
              </div>
              <h4 className="release-notes-entry-title">{release.title}</h4>
              <ul className="release-notes-items">
                {release.items.map((item, i) => (
                  <li key={i}>{item}</li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default ReleaseNotesModal;
